import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Hyperlink from '@ingka/hyperlink';
import Button from '@ingka/button';
import Text from '@ingka/text';
import { getLoginLink } from '../helpers/loginLinkHelper';
import { isBrowser } from '../helpers/browserHelper';
import {
  AvailableService,
  PriceOfferType,
} from '../services/Appointment/model';
import { useStateSelector } from '../store';
import { LoginPrompt } from './LoginPrompt';
import TextWithPrice from './TextWithPrice';
import { isIkeaMembershipSavings } from '../helpers/currencyHelper';
import { isBusinessOperation } from '../helpers/urlHelper';
import { CustomerType } from '../reducers/user/userTypes';
import useTranslation from '../hooks/useTranslation';
import { selectOperationForTheSelectedTimeSlot } from '../reducers/selectors';

const StyledText = styled(Text)`
  &&& {
    display: inline-flex;
    margin-top: 0;
    margin-bottom: 2.5rem;
  }
`;

const StyledLoginText = styled(Text)`
  &&& {
    margin-bottom: 1rem;
  }
`;

type LoginButtonProps = {
  $marginBottom: string;
};

const StyledLoginButton = styled(Button)<LoginButtonProps>`
  min-width: 300px;
  margin-bottom: ${({ $marginBottom }) => $marginBottom};
`;

interface LoginLinkProps {
  service?: AvailableService;
  isButton?: boolean;
  showLoginPrompt?: boolean;
}

const getLoginLinkTextKey = (priceOfferType: string): string =>
  priceOfferType === 'family'
    ? 'pages.start.loginLinkTextFamilyPrice'
    : 'pages.start.loginLinkTextBusinessNetworkPrice';

const getLoginLinkIdentifier = (priceOfferType: string): string =>
  priceOfferType === 'family'
    ? 'familyPriceLoginLink'
    : 'businessNetworkPriceLoginLink';

const hideLoginLink = (
  userIsLoggedIn: boolean,
  allowedMemberDiscounts: boolean,
  customerType: CustomerType,
  isBusiness: boolean,
  priceOfferType?: PriceOfferType,
): boolean => {
  if (!priceOfferType || priceOfferType === 'tro') {
    return userIsLoggedIn;
  }
  const isIkeaMembership = userIsLoggedIn && allowedMemberDiscounts;
  return isBusiness
    ? isIkeaMembership && customerType === 'Business'
    : isIkeaMembership;
};

const LoginLink: React.FC<LoginLinkProps> = ({
  service,
  isButton,
  showLoginPrompt,
}): JSX.Element | null => {
  const { t } = useTranslation();
  const [loginUrl, setLoginUrl] = useState('');
  const userIsLoggedIn = useStateSelector((state) => state.user.isLoggedIn);
  const allowedMemberDiscounts = useStateSelector(
    (state) => state.user.allowedMemberDiscounts,
  );
  const customerType = useStateSelector((state) => state.user.customerType);
  const operation = useStateSelector(selectOperationForTheSelectedTimeSlot);

  const { priceOfferType, inclSavingsFormatted } = service?.displayPrice || {};
  const showMemberPriceLink =
    !!inclSavingsFormatted &&
    !!priceOfferType &&
    isIkeaMembershipSavings(priceOfferType);

  useEffect(() => {
    if (isBrowser() && !userIsLoggedIn) {
      const url = getLoginLink(window.location.toString(), CSS_CONFIG);
      setLoginUrl(url);
    }
  }, [userIsLoggedIn]);

  return hideLoginLink(
    userIsLoggedIn,
    allowedMemberDiscounts,
    customerType,
    isBusinessOperation(operation, CSS_CONFIG),
    priceOfferType,
  ) ? null : (
    <>
      {!isButton && (
        <StyledText tagName="p" textStyle="Body.L" data-cy="login-link">
          <Hyperlink
            id="loginHyperlink"
            url={loginUrl}
            text={
              showMemberPriceLink ? (
                <TextWithPrice
                  textKey={getLoginLinkTextKey(priceOfferType)}
                  price={inclSavingsFormatted}
                />
              ) : (
                t('pages.start.loginLinkText')
              )
            }
            colour="black"
            data-cy={
              showMemberPriceLink
                ? getLoginLinkIdentifier(priceOfferType)
                : 'regularLoginLink'
            }
          />
        </StyledText>
      )}
      {isButton && (
        <>
          <StyledLoginText tagName="p" textStyle="Body.L" data-cy="login-link">
            {t('pages.start.loginLinkText')}
          </StyledLoginText>
          <StyledLoginButton
            id="loginButton"
            type="primary"
            text={t('pages.start.loginButtonText')}
            href={loginUrl}
            data-cy="loginButton"
            $marginBottom={showLoginPrompt ? '0.5rem' : '2.5rem'}
          />
        </>
      )}
      {showLoginPrompt && <LoginPrompt />}
    </>
  );
};

export default LoginLink;
